import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Modal } from '@garnish/overlays';

import { GroupOrderConfirmationContent } from '../../../GroupOrderConfirmationContent';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderBagLeaveModal = (props: Props) => {
  const { visible, isLeavingGroupOrder, onClose, onConfirm } = props;

  const { formatMessage } = useIntl();

  // ─── Handlers ──────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isLeavingGroupOrder) return;

    onClose();
  }, [isLeavingGroupOrder, onClose]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={visible}
      size="small"
      withSafeAreaView={false}
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      onRequestClose={handleClose}
    >
      <GroupOrderConfirmationContent
        title={<FormattedMessage {...messages.title} />}
        body={<FormattedMessage {...messages.body} />}
        dismissButtonLabel={<FormattedMessage {...messages.stayCta} />}
        confirmButtonLabel={<FormattedMessage {...messages.leaveOrderCta} />}
        confirmButtonPalette="caution"
        isPerformingAction={isLeavingGroupOrder}
        onClose={handleClose}
        onConfirm={onConfirm}
      />
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessibilityLabel: {
    defaultMessage: 'Leave group order confirmation',
    description: 'Bag | Group order | Leave modal | Accessibility label',
  },
  title: {
    defaultMessage: 'Leave group order?',
    description: 'Bag | Group order | Leave modal | Title',
  },
  body: {
    defaultMessage: 'If you leave, your items will be removed and the host will be notified.',
    description: 'Bag | Group order | Leave modal | Body',
  },
  stayCta: {
    defaultMessage: 'Stay',
    description: 'Bag | Group order | Leave modal | Stay CTA',
  },
  leaveOrderCta: {
    defaultMessage: 'Leave order',
    description: 'Bag | Group order | Leave modal | Confirm CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  visible: boolean;
  isLeavingGroupOrder: boolean;
  onClose: () => void;
  onConfirm: () => void;
};
